import {
  type BackupSchedule,
  type ClassificationPolicySchedule,
  type ClassificationScheduleConfig,
  ClassificationScheduleFrequency,
  DayOfWeek,
  ScheduleMode,
  ScheduleTimezone,
  ScheduleType,
} from '@repo/api-gw-sdk';
import { v4 as uuid } from 'uuid';

import type { ResourceSelectionState } from '@/components/resourcesSelection/resourceSelectionState';
import { conditionSelectionToResourceSelectorExpression } from '@/components/resourcesSelection/resourceSelectorAdapter';

import {
  annuallyFrequency,
  dailyFrequency,
  deconstructCron,
  getFrequencyByWindow,
  monthlyFrequency,
  weeklyFrequency,
} from '../frequency';

const DEFAULT_START_WINDOW_MINUTES = 240;

const dayOfWeekByCronValue: Record<string, DayOfWeek> = {
  '0': DayOfWeek.Sun,
  '1': DayOfWeek.Mon,
  '2': DayOfWeek.Tue,
  '3': DayOfWeek.Wed,
  '4': DayOfWeek.Thu,
  '5': DayOfWeek.Fri,
  '6': DayOfWeek.Sat,
  '7': DayOfWeek.Sun,
};

const cronDayOfWeekByDay: Record<DayOfWeek, number> = {
  [DayOfWeek.Sun]: 0,
  [DayOfWeek.Mon]: 1,
  [DayOfWeek.Tue]: 2,
  [DayOfWeek.Wed]: 3,
  [DayOfWeek.Thu]: 4,
  [DayOfWeek.Fri]: 5,
  [DayOfWeek.Sat]: 6,
};

const firstValue = (value: string) => value.split(',')[0] || '0';

const toTimeOfDay = (hour: string, minute: string) => ({
  hour: Number(hour) || 0,
  minute: Number(minute) || 0,
});

export interface ClassificationPolicyEditState {
  id: string;
  name: string;
  focusOnName: boolean;
  resourceSelection: ResourceSelectionState;
  schedule: BackupSchedule;
  mode: ScheduleMode;
  scheduleTimezone: ScheduleTimezone;
}

export const getDefaultClassificationSchedule = (): BackupSchedule => ({
  id: uuid(),
  duration: 240,
  backupRetention: 0,
  vaultId: '',
  windows: dailyFrequency.windows,
  scheduleType: ScheduleType.SCHEDULE_TYPE_STANDARD,
});

// The classification schedule is deliberately modeled separately from the
// standard backup schedule: classification scans have no sub-daily cadence.
export const scheduleConfigToBackupSchedule = (
  schedule: ClassificationPolicySchedule | null | undefined
): BackupSchedule => {
  const base = getDefaultClassificationSchedule();
  const config = schedule?.scheduleConfig;
  if (!config) {
    return base;
  }

  if (
    config.frequency ===
      ClassificationScheduleFrequency.CLASSIFICATION_SCHEDULE_WEEKLY &&
    config.weeklyConfig
  ) {
    const daysOfWeek = Array.from(config.weeklyConfig.daysOfWeek ?? []);
    if (daysOfWeek.length) {
      const time = config.weeklyConfig.timeOfDay;
      return {
        ...base,
        windows: `${time?.minute ?? 0} ${time?.hour ?? 0} * * ${daysOfWeek
          .map((day) => cronDayOfWeekByDay[day])
          .join(',')}`,
        duration:
          config.weeklyConfig.startWindowMinutes ??
          DEFAULT_START_WINDOW_MINUTES,
      };
    }
  }

  if (
    config.frequency ===
      ClassificationScheduleFrequency.CLASSIFICATION_SCHEDULE_MONTHLY &&
    config.monthlyConfig
  ) {
    const daysOfMonth = Array.from(config.monthlyConfig.daysOfMonth ?? []);
    if (daysOfMonth.length) {
      const time = config.monthlyConfig.timeOfDay;
      return {
        ...base,
        windows: `${time?.minute ?? 0} ${time?.hour ?? 0} ${daysOfMonth.join(',')} * *`,
        duration:
          config.monthlyConfig.startWindowMinutes ??
          DEFAULT_START_WINDOW_MINUTES,
      };
    }
  }

  if (
    config.frequency ===
      ClassificationScheduleFrequency.CLASSIFICATION_SCHEDULE_ANNUALLY &&
    config.annuallyConfig
  ) {
    const time = config.annuallyConfig.timeOfDay;
    const timeOfYear = config.annuallyConfig.timeOfYear;
    return {
      ...base,
      windows: `${time?.minute ?? 0} ${time?.hour ?? 0} ${timeOfYear?.dayOfMonth ?? 1} ${timeOfYear?.month ?? 1} *`,
      duration:
        config.annuallyConfig.startWindowMinutes ??
        DEFAULT_START_WINDOW_MINUTES,
    };
  }

  const time = config.dailyConfig?.timeOfDay;
  return {
    ...base,
    windows: `${time?.minute ?? 0} ${time?.hour ?? 0} * * *`,
    duration:
      config.dailyConfig?.startWindowMinutes ?? DEFAULT_START_WINDOW_MINUTES,
  };
};

export const backupScheduleToClassificationScheduleConfig = (
  schedule: BackupSchedule
): ClassificationScheduleConfig => {
  const frequency = getFrequencyByWindow(schedule.windows);
  const { minutes, hours, dayOfMonth, month, dayOfWeek } = deconstructCron(
    schedule.windows
  );
  const timeOfDay = toTimeOfDay(firstValue(hours), firstValue(minutes));
  const startWindowMinutes = schedule.duration || DEFAULT_START_WINDOW_MINUTES;

  if (frequency === weeklyFrequency) {
    return {
      frequency: ClassificationScheduleFrequency.CLASSIFICATION_SCHEDULE_WEEKLY,
      weeklyConfig: {
        daysOfWeek: new Set(
          dayOfWeek
            .split(',')
            .map((day) => dayOfWeekByCronValue[day])
            .filter((day) => !!day)
        ),
        timeOfDay,
        startWindowMinutes,
      },
    };
  }

  if (frequency === monthlyFrequency) {
    return {
      frequency:
        ClassificationScheduleFrequency.CLASSIFICATION_SCHEDULE_MONTHLY,
      monthlyConfig: {
        daysOfMonth: new Set(
          dayOfMonth.split(',').map((day) => Number(day) || 1)
        ),
        timeOfDay,
        startWindowMinutes,
      },
    };
  }

  if (frequency === annuallyFrequency) {
    return {
      frequency:
        ClassificationScheduleFrequency.CLASSIFICATION_SCHEDULE_ANNUALLY,
      annuallyConfig: {
        timeOfDay,
        timeOfYear: {
          month: Number(month) || 1,
          dayOfMonth: Number(dayOfMonth) || 1,
        },
        startWindowMinutes,
      },
    };
  }

  return {
    frequency: ClassificationScheduleFrequency.CLASSIFICATION_SCHEDULE_DAILY,
    dailyConfig: {
      timeOfDay,
      startWindowMinutes,
    },
  };
};

export const buildClassificationPolicyFields = (
  state: ClassificationPolicyEditState
) => ({
  name: state.name,
  resourceSelector: conditionSelectionToResourceSelectorExpression(
    state.resourceSelection
  ),
  schedule: {
    scheduleConfig: backupScheduleToClassificationScheduleConfig(
      state.schedule
    ),
  },
  scheduleMode: state.mode,
  scheduleTimezone:
    state.mode === ScheduleMode.SCHEDULE_MODE_ADVANCED
      ? state.scheduleTimezone
      : ScheduleTimezone.SCHEDULE_TIMEZONE_RESOURCE,
});
